import { useEffect, useMemo, type MutableRefObject } from 'react';
import { usePageInfo } from '@op/hooks/common';
import { transformXResponseToMatrix } from '../transformMatrix';
import type { IXHashtagApiFilter } from '../types';
import { useXHashtagList } from './useXHashtagList';

export const X_HASHTAG_DEFAULT_PAGE_INFO = { page: 1, page_size: 20 };

interface IUseXHashtagMatrixArgs {
  filter: IXHashtagApiFilter | null;
  onHasDataDate?: (date: string) => void;
  onHasDownloadChange?: (hasDownload: boolean) => void;
  downloadRef?: MutableRefObject<(() => void) | null>;
  isFirstRequestRef?: MutableRefObject<boolean>;
}

export function useXHashtagMatrix({
  filter,
  onHasDataDate,
  onHasDownloadChange,
  downloadRef,
  isFirstRequestRef,
}: IUseXHashtagMatrixArgs) {
  const { pageInfo, changePageInfo, resetPageInfo } = usePageInfo({
    defaultPageInfo: X_HASHTAG_DEFAULT_PAGE_INFO,
  });

  const { data, total, loading, groupBy } = useXHashtagList({
    filter,
    onHasDataDate,
    onHasDownloadChange,
    downloadRef,
    isFirstRequestRef,
  });

  const matrixView = useMemo(
    () => transformXResponseToMatrix(data),
    [data],
  );

  const rowTotal = matrixView.rows.length;

  const paginatedRows = useMemo(() => {
    const start = (pageInfo.page - 1) * pageInfo.page_size;
    return matrixView.rows.slice(start, start + pageInfo.page_size);
  }, [matrixView.rows, pageInfo.page, pageInfo.page_size]);

  useEffect(() => {
    resetPageInfo();
  }, [filter]);

  return {
    matrixView,
    paginatedRows,
    rowTotal,
    total,
    loading,
    groupBy,
    pageInfo,
    changePageInfo,
  };
}
